import { formatStr } from "@lib/angular/measurement/format-str";
import { measurement } from "@lib/measurement";
import { WherePrimitive, WhereTable } from "@lib/where";
import { DateTime } from "@lib/date-time";
import { Observable } from "@lib/rxjs";
import { asFiniteNumber } from "@lib/number";


export const addSelectFilterConfig = <T>(column: string, dbColumn: string, isValueLabelPair: boolean,options: FilterValueOption[]): ModalFilter => {
    return {
        type: "select-string",
        column,
        dbColumn,
        isValueLabelPair,
        options
    };
}

export const addMeasurmentFilter = (value: string, column: string, dbColumn: string, typeToUnit: any): ModalFilter => {
    const parsedValues = value.split(",")
        .map(num => asFiniteNumber(num.trim()))
        .filter(num => !isNaN(num))
        .map(num => Math.round(num * 100) / 100);

    const formattedValues = formatStr(parsedValues, "distance", typeToUnit, 0, '${v}', false).split(",");
    return {
        type: "select-number",
        column,
        dbColumn,
        isValueLabelPair:true,
        options: formattedValues.map((val, i) => ({
            value: parsedValues[i],
            label: `${val} ${measurement.abbreviation(typeToUnit["distance"])}`
        }))
    };
}

export const convertFiltersToWhereTable = (filters: FilterConfig[]): WhereTable => {
	
	const operatorMap: Record<string, '=' | '<' | '<=' | '>' | '>=' | 'in' | 'like'> = {
		"Is": "=",
        "Greater Than": ">",
        "Greater Than or Equal To": ">=",
        "Less Than": "<",
        "Less Than or Equal To": "<=",
        "In": "in",
        "Like": "like"
    };
	
    const whereTable: any = {};

    for (const filter of filters) {
		const column = filter.dbColumn
        const operator = operatorMap[filter.type];
		
        if (!operator) {
			throw new Error(`Unsupported filter type: ${filter.type}`);
        }

        // Convert value to number if applicable
        let value: WherePrimitive = asFiniteNumber(filter.value) ?? filter.value;

        // Ensure `whereTable[column]` is initialized as an array
        if (!Array.isArray(whereTable[column])) {
            whereTable[column] = [];
        }

		if (column === "begin" || column === "end") {
            value = asFiniteNumber(value) / 1000
		}

        // Handle 'in' operator separately
        if (operator === "in") {
            whereTable[column].push(['in', Array.isArray(value) ? value : [value]]);
        } else if (operator === 'like') {
            whereTable[column].push([operator, value.toString()]);
        } else {
            whereTable[column].push([operator, value]);
        }
    }

    return whereTable;
}

export const createSearchWhereTable = (searchTerms: { field: string; value: any }[]): WhereTable => {
    const searchWhere: WhereTable = {};

    if (searchTerms && searchTerms.length) {
        searchTerms.forEach((filter) => {
            const filterField = filter.field;
            const filterKeyword = filter.value;

            if (filterField && filterKeyword) {
                searchWhere[filterField] = [['like', `${filterKeyword}`]]; // Wrap in an array to match WhereCondition[]
            }
        });
    }

    return searchWhere;
}

export interface FilterConfig {
	column: string,
	dbColumn :string,
	type: string,
	value: string | DateTime | number,
	label? : string | number,
    spec: FilterModalType
    unitSystem$?: Observable<keyof measurement.ExtendedTypeMeta>
}

export interface Filter {
	field:string,
	value:string	
}

export type measurementTypes = measurement.Type;
export type FilterModalType = 'select-string'
    | 'select-number'
    | 'input-string'
    | 'input-number'
    | 'date'
    | `measurement-${Extract<measurementTypes, string>}`;

export type FilterValueOption = string | { value: string | number; label: string };

export interface ModalFilter {
	column: string;
	dbColumn : string;
	type: FilterModalType;
    isValueLabelPair?: boolean;
	options?: FilterValueOption[];
    unitSystem$?: Observable<keyof measurement.ExtendedTypeMeta>
}

export const filterTypes = [
	{
		type:"input-number",
		values: ["Choose filter..", 'Is', "Greater Than", "Greater Than or Equal To", "Less Than or Equal To", "Less Than"]
	},
    {
        type:"select-number",
        values: ["Choose filter..",'Is', "Greater Than", "Greater Than or Equal To", "Less Than or Equal To", "Less Than"]
    },
    {
        type:"date",
        values: ["Choose filter.." , "Greater Than", "Greater Than or Equal To", "Less Than or Equal To", "Less Than"]
    },
    {
        type:"measurement",
        values: ["Choose filter..", "Greater Than", "Less Than"]
    },
    {
        type:"input-string",
        values: ["Choose filter..", 'Is', 'Like']
    },
    {
        type:"select-string",
        values: ["Choose filter..", 'Is']
    },
]
